<script setup>
import { createInput, FormKit } from "@formkit/vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import cloneDeep from "lodash/cloneDeep";
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import globals from "../globals";
import userService from "../services/user.service.ts";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "./ui/dialog";
import PasswordMeter from "./PasswordMeter.vue";
import { useAdminStore } from "../pinia/admin.ts";
import ToggleButtons from "./ui/formkit/ToggleButtons.vue";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/vue/24/solid";

const toggleButtons = createInput(ToggleButtons, {
  props: ["options"],
});

const { t } = useI18n();

const adminStore = useAdminStore();

const userCount = computed(() => adminStore.user?.UserCount);
const userLimit = computed(() => adminStore.user?.Licensing?.UserLimit);
const userOverage = computed(() => adminStore.user?.Licensing?.UserLimitOverage);
const userOveragePercentage = computed(() => adminStore.user?.Licensing?.UserLimitOveragePercentage);

const userLimitReached = computed(() => userCount.value >= userLimit.value + userOverage.value);
const maxSessionLimit = computed(() => adminStore.user.Licensing.SessionLimit);

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const createNewApiKey = ref(true);
const allowedFrom = ref([
  { prefix: "0.0.0.0/0", note: "Allow entire Internet IPv4" },
  { prefix: "::/0", note: "Allow entire Internet IPv6" },
]);
const newAllowedFromPrefix = ref("");
const newAllowedFromNote = ref(null);
const newAllowedFromError = ref("");

const addAllowedFrom = () => {
  if (validCidr(newAllowedFromPrefix.value)) {
    allowedFrom.value.push({ prefix: newAllowedFromPrefix.value, note: newAllowedFromNote.value });
    newAllowedFromPrefix.value = "";
    newAllowedFromNote.value = null;
    newAllowedFromError.value = "";
  } else {
    newAllowedFromError.value = t("err.invalidCidr");
  }
};

const notePlaceholders = [
  "Mallory's hoodie",
  "Alice's house",
  "Bruce's office",
  "Charlie's cafe",
  "Darla's datacenter",
];
const randomNotePlaceholder = ref(notePlaceholders[Math.floor(Math.random() * notePlaceholders.length)]);

const validCidr = (cidr) => {
  const octet = "(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])";
  const sextet = "[0-9a-fA-F]{1,4}";
  const v4cidr = "/(3[0-2]|[21][0-9]|[0-9])";
  const ipv4 = `(${octet}\\.){3}${octet}${v4cidr}`;
  const v6cidr = "/(12[0-8]|1[01][0-9]|[1-9][0-9]|[0-9])";
  const ipv6 = `
	  (
	   (${sextet}:){7}${sextet}|
	   (${sextet}:){1,7}:|
	   (${sextet}:){1,6}:${sextet}|
	   (${sextet}:){1,5}(:${sextet}){1,2}|
	   (${sextet}:){1,4}(:${sextet}){1,3}|
	   (${sextet}:){1,3}(:${sextet}){1,4}|
	   (${sextet}:){1,2}(:${sextet}){1,5}|
	   ${sextet}:((:${sextet}){1,6})|
	   :(((:${sextet}){1,7})|:)|
	   fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|
	   ::(ffff(:0{1,4}){0,1}:){0,1}${ipv4}|
	   (${sextet}:){1,4}:${ipv4}
	  )${v6cidr}`;
  const ipv46Regex = new RegExp(`^(${ipv4}|${ipv6})$`.replace(/\s+/g, ""));

  return ipv46Regex.test(cidr);
};

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  onToggleShow: {
    type: Function,
  },
  onSave: {
    type: Function,
  },
  value: {
    type: Object,
  },
});

const userForm = ref(props.value ? cloneDeep(props.value) : {});
const hasUserNameBeenModified = ref(false);

watchEffect(() => {
  if (props.value) {
    if (userForm.value?.ValidUntil) {
      userForm.value.ValidUntil = globals.formatDateTime(userForm.value.ValidUntil);
    }
  }

  if (!props.value && !hasUserNameBeenModified.value && userForm.value?.Name) {
    userForm.value.UserName = userForm.value.Name.toLowerCase().split(" ").join(".");
  }
});

function passwordsMustMatch(node) {
  return node.value === node.at("Password").value;
}

function handleToggleShow() {
  props.onToggleShow?.();
}

async function handleSubmit(fields) {
  if (Boolean(props.value)) {
    handleEdit(fields);
    return;
  }

  handleCreate(fields);
}

async function handleCreate(fields) {
  try {
    const userResponse = await userService.createUser(
      JSON.stringify({
        Name: fields.Name,
        Company: fields.Company,
        Duration: fields.Duration,
        SessionLimit: fields.SessionLimit,
        AuthType: fields.AuthType,
        UserName: fields.UserName,
        PlainPassword: fields.Password,
        TOTPEnabled: fields.TOTPEnabled,
        ValidUntil: globals.marshalDateTime(fields.ValidUntil),
        ThirdPartyAuth: fields.ThirdPartyAuth,
      }),
    );

    const userId = userResponse.data;
    if (fields.AuthType === "apikey" && createNewApiKey.value) {
      await createApiKey(fields.NewApiKeyName, fields.NewApiKeyExpiredAfter, allowedFrom.value, userId);
    }

    handleToggleShow();
    props.onSave?.([userResponse.data, null]);
  } catch (err) {
    props.onSave?.([null, err, false]);
  }
}

async function handleEdit(fields) {
  try {
    const userResponse = await userService.updateUser(
      props.value.ID,
      JSON.stringify({
        ID: props.value.ID,
        Name: fields.Name,
        Company: fields.Company,
        Duration: fields.Duration,
        SessionLimit: fields.SessionLimit,
        AuthType: fields.AuthType,
        UserName: fields.UserName,
        PlainPassword: fields.Password,
        TOTPEnabled: fields.TOTPEnabled,
        ValidUntil: globals.marshalDateTime(fields.ValidUntil),
        ThirdPartyAuth: fields.ThirdPartyAuth,
        AclIds: fields.AclIds || [],
      }),
    );

    handleToggleShow();
    props.onSave?.([userResponse.data, null, true]);
  } catch (err) {
    props.onSave?.([null, err]);
  }
}

async function createApiKey(name, expiredAfter, allowedFrom, userId) {
  let data = {
    Name: name,
    AllowedFrom: allowedFrom,
    Scopes: [`user.auth:${userId}`],
  };

  if (expiredAfter === "12months") {
    data.ExpiredAfter = new Date();
    data.ExpiredAfter.setFullYear(data.ExpiredAfter.getFullYear() + 1);
  } else if (expiredAfter === "24months") {
    data.ExpiredAfter = new Date();
    data.ExpiredAfter.setFullYear(data.ExpiredAfter.getFullYear() + 2);
  }

  try {
    await userService.createApiKey(JSON.stringify(data));
  } catch (err) {
    props.onSave?.([null, err, false]);
  }
}

let adminInterval;

onMounted(() => {
  adminStore.fetchStatus();
  adminInterval = setInterval(async () => adminStore.fetchStatus(), 3 * 60 * 1000);
});

onUnmounted(() => {
  clearInterval(adminInterval);
});
</script>

<template>
  <Dialog :show="props.show">
    <DialogOverlay />

    <DialogPanel theme="rose">
      <DialogTitle>
        {{ Boolean(props.value) ? t("pages.users.dialogs.edit.heading") : t("pages.users.dialogs.create.heading") }}
      </DialogTitle>

      <FormKit
        v-model="userForm"
        type="form"
        :actions="false"
        @submit="handleSubmit"
        @submit-invalid="globals.focusFirstError"
      >
        <div class="flex flex-col">
          <!-- If trying to create but license would exceed -->
          <p v-if="!Boolean(props.value) && userLimitReached">
            {{
              t("err.userLimitReached", {
                userOveragePercentage,
                limit: userLimit,
              })
            }}
            <a href="https://licensing.knocknoc.io" class="text-[#e05454]" target="_blank">licensing.knocknoc.io</a>
          </p>

          <!-- If license not exceeded or we're just editing -->
          <template v-else>
            <div class="grid grid-cols-2 gap-4">
              <FormKit
                name="Name"
                :label="t('pages.users.forms.create.fields.Name.label')"
                type="text"
                validation="required"
                :autocomplete="autoComplete"
              />

              <FormKit
                name="UserName"
                :disabled="Boolean(props.value)"
                :label="t('pages.users.forms.create.fields.UserName.label')"
                :placeholder="t('pages.users.forms.create.fields.UserName.placeholder')"
                type="text"
                validation="required"
                @focus="hasUserNameBeenModified = true"
                :autocomplete="autoComplete"
              />
            </div>

            <FormKit
              name="AuthType"
              :label="t('pages.users.forms.create.fields.AuthType.label')"
              :options="
                ['local', 'SAML', 'LDAP', 'apikey'].map((option) => ({
                  value: option,
                  label: t(`pages.users.forms.create.fields.AuthType.options.${option}`),
                }))
              "
              type="select"
            />

            <template v-if="userForm.AuthType === 'local'">
              <div class="mb-4 flex flex-col">
                <div class="grid grid-cols-2 gap-4">
                  <FormKit
                    :classes="{
                      outer: 'max-w-full mb-2',
                    }"
                    name="Password"
                    :label="t('pages.users.forms.create.fields.Password.label')"
                    type="password"
                    :validation="props.value ? [['matches', /^$|^.{8,64}$/]] : 'required|length:8,64'"
                    :validation-messages="{ matches: 'Must be between 8 and 64 characters' }"
                    autocomplete="off"
                  />

                  <FormKit
                    :classes="{
                      outer: 'max-w-full mb-2',
                    }"
                    name="ConfirmPassword"
                    :label="t('pages.users.forms.create.fields.ConfirmPassword.label')"
                    type="password"
                    :validation="props.value ? 'passwordsMustMatch' : 'required|passwordsMustMatch'"
                    :validation-rules="{ passwordsMustMatch }"
                    :validation-messages="{
                      passwordsMustMatch: t('err.passwordsDontMatch'),
                    }"
                    autocomplete="off"
                  />
                </div>

                <PasswordMeter :value="userForm.Password" />
              </div>

              <FormKit
                name="ValidUntil"
                :label="t('pages.users.forms.create.fields.ValidUntil.label')"
                type="datetime-local"
                @input="
                  (val) => {
                    if (!val && userForm.value) {
                      userForm.value.ValidUntil = null;
                    }
                  }
                "
              />
            </template>
            <template v-else-if="userForm.AuthType === 'LDAP'">
              <FormKit name="ThirdPartyAuth" type="group">
                <FormKit
                  name="DN"
                  :label="t('pages.users.forms.create.fields.ThirdPartyAuth.fields.DN.label')"
                  :placeholder="t('pages.users.forms.create.fields.ThirdPartyAuth.fields.DN.placeholder')"
                  type="text"
                  validation="required"
                  :autocomplete="autoComplete"
                />
              </FormKit>
            </template>
            <template v-else-if="userForm.AuthType === 'SAML'">
              <div
                class="border-warning/50 text-warning dark:border-warning [&>svg]:text-warning relative mb-4 w-full rounded-lg border p-4 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7"
                role="alert"
              >
                <ExclamationTriangleIcon class="h-4 w-4" />
                <p class="text-sm [&_p]:leading-relaxed">
                  {{ t("pages.users.forms.create.fields.AuthType.samlWarning") }}
                </p>
              </div>
            </template>
            <template v-else-if="userForm.AuthType === 'apikey'">
              <FormKit
                :classes="{
                  outer: 'grow-0',
                }"
                v-model="createNewApiKey"
                name="CreateNewApiKey"
                :options="
                  ['new', 'none'].map((option) => ({
                    label: t(`pages.users.forms.create.fields.AuthType.createApiKey.${option}`),
                    value: option === 'new',
                  }))
                "
                :type="toggleButtons"
              />

              <template v-if="createNewApiKey">
                <div class="grid grid-cols-2 gap-4">
                  <FormKit
                    type="text"
                    name="NewApiKeyName"
                    :label="t('pages.users.forms.create.fields.AuthType.createApiKey.Name.label')"
                    validation="required"
                    :autocomplete="autoComplete"
                  />

                  <FormKit
                    type="select"
                    name="NewApiKeyExpiredAfter"
                    :label="t('pages.users.forms.create.fields.AuthType.createApiKey.Expires.label')"
                    :options="{
                      '12months': t('in 12 months'),
                      '24months': t('in 24 months'),
                      never: t('never'),
                    }"
                    validation="required"
                  />
                </div>

                <label
                  class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300"
                  for="allowedFromNewPrefix"
                >
                  {{ t("fields.apiKeys.allowedFrom") }}
                </label>
                <div class="mb-4 grid grid-cols-3">
                  <template v-for="(allowedFromEntry, index) in allowedFrom">
                    <span class="col-span-2">
                      {{ allowedFromEntry.prefix }}
                      {{ allowedFromEntry.note ? `(${allowedFromEntry.note})` : "" }}
                    </span>
                    <button type="button" class="p-1" @click="allowedFrom.splice(index, 1)" :title="t('msg.remove')">
                      <MinusCircleIcon class="inline h-5 w-5 text-[#e05454]" />
                    </button>
                  </template>
                  <div class="relative mr-1">
                    <input
                      class="peer block border-0 bg-background py-1.5 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                      type="text"
                      v-model="newAllowedFromPrefix"
                      placeholder="192.0.2.0/24"
                      id="allowedFromNewPrefix"
                      :autocomplete="autoComplete"
                    />
                    <div
                      class="absolute inset-x-0 bottom-0 border-t border-[#ccd1c4] peer-focus:border-t-2 peer-focus:border-[#e05454]"
                      aria-hidden="true"
                    ></div>
                  </div>
                  <div class="relative">
                    <input
                      class="peer block border-0 bg-background py-1.5 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                      type="text"
                      v-model="newAllowedFromNote"
                      :placeholder="randomNotePlaceholder"
                      id="allowedFromNewNote"
                      :autocomplete="autoComplete"
                    />
                    <div
                      class="absolute inset-x-0 bottom-0 border-t border-[#ccd1c4] peer-focus:border-t-2 peer-focus:border-[#e05454]"
                      aria-hidden="true"
                    ></div>
                  </div>
                  <button type="button" class="p-1" @click="addAllowedFrom" :title="t('msg.add')">
                    <PlusCircleIcon class="inline h-5 w-5 text-[#e05454]" />
                  </button>
                </div>

                <p v-if="newAllowedFromError" id="newAllowedFromError" class="mb-4 text-xs text-[#e05454]">
                  {{ newAllowedFromError }}
                </p>
              </template>
            </template>

            <FormKit
              name="Company"
              :label="t('pages.users.forms.create.fields.Company.label')"
              type="text"
              :autocomplete="autoComplete"
            />

            <div class="grid grid-cols-2 gap-4">
              <FormKit
                name="Duration"
                :label="t('pages.users.forms.create.fields.Duration.label')"
                :help="t('pages.users.forms.create.fields.Duration.help')"
                min="1"
                number="integer"
                type="number"
                validation="required|min:1"
                value="480"
                :autocomplete="autoComplete"
              />

              <FormKit
                name="SessionLimit"
                :label="t('pages.users.forms.create.fields.SessionLimit.label')"
                :help="
                  t('pages.users.forms.create.fields.SessionLimit.help', {
                    max: maxSessionLimit,
                  })
                "
                number="integer"
                min="1"
                :max="maxSessionLimit"
                type="number"
                :validation="'required|min:1|max:' + maxSessionLimit"
                :value="maxSessionLimit"
                :autocomplete="autoComplete"
              />
            </div>

            <FormKit
              name="TOTPEnabled"
              :label="t('pages.users.forms.create.fields.TOTPEnabled.label')"
              type="checkbox"
              v-if="userForm.AuthType !== 'SAML'"
            />
          </template>

          <div class="flex space-x-3">
            <button
              type="button"
              @click="handleToggleShow"
              class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
            >
              {{ t("common.cancel") }}
            </button>

            <button
              v-if="!userLimitReached || Boolean(props.value)"
              type="submit"
              class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
            >
              {{ Boolean(props.value) ? t("common.save") : t("common.create") }}
            </button>
          </div>
        </div>
      </FormKit>
    </DialogPanel>
  </Dialog>
</template>
